import type { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import type { ISyncOperation } from "@/store/sync/operations/types";
import { AcceptNoteSuggestionOperation } from "@/store/sync/operations/notes/AcceptNoteSuggestionOperation";
import voiceModeImage from "@/assets/images/guide-card-voice-mode.gif";
import meetingRecordingImage from "@/assets/images/guide-card-meetings-how-to.gif";

/**
 * Type-safe step identifiers for getting started guide.
 * Using const assertion for compile-time type safety and IDE autocomplete.
 */
export const STEP_IDS = {
  ACCEPT_VOICE_CHANGES: "accept_voice_changes",
  RECORD_MEETING: "record_meeting",
  SEND_CHAT_MESSAGE: "send_chat_message",
  GET_APP_OR_EXTENSION: "get_app_or_extension",
} as const;

/**
 * Help content for steps that have contextual help
 */
export interface StepHelpContent {
  title: string;
  description: string;
  image: string;
}

/**
 * Configuration interface for getting started steps
 */
export interface StepConfig {
  id: StepId;
  title: string;
  /**
   * Optional tooltip text shown on hover
   */
  tooltip?: string;
  /**
   * Optional function to match sync operations for automatic completion detection.
   * If not provided, the step must be completed manually via markStepComplete().
   */
  matchesOperation?: (operation: BaseSyncOperation<ISyncOperation>) => boolean;
  /**
   * Optional help content shown in modal when user clicks the help icon
   */
  helpContent?: StepHelpContent;
}

/**
 * Runtime step data interface
 */
export interface Step {
  id: StepId;
  title: string;
  completedAt?: Date;
}

/**
 * Configuration for all getting started steps.
 * Steps are processed in order and displayed to users in this sequence.
 */
export const STEP_CONFIGS: StepConfig[] = [
  {
    id: STEP_IDS.ACCEPT_VOICE_CHANGES,
    title: "Brain dump your tasks with Voice Mode",
    tooltip: "Completes when you accept a note created from Voice Mode",
    matchesOperation: operation => operation instanceof AcceptNoteSuggestionOperation,
    helpContent: {
      title: "How to take a note with Voice Mode",
      description:
        "Brain dump your tasks this week. Create a note and simply start talking—Mem will automatically transcribe and create organized notes for you.",
      image: voiceModeImage,
    },
  },
  {
    id: STEP_IDS.RECORD_MEETING,
    title: "Record a meeting and let Mem take notes",
    tooltip: "Completes when you start Voice Mode on a note with a calendar event",
    helpContent: {
      title: "Recording a meeting with Mem",
      description:
        "Connect your calendar to see your meetings right in Mem. Join your meeting from the desktop notification or from Mem.",
      image: meetingRecordingImage,
    },
  },
  {
    id: STEP_IDS.SEND_CHAT_MESSAGE,
    title: "Ask Chat about your meeting note",
    tooltip: "Completes when you use Chat",
    // No matchesOperation - manually triggered from MemItPageStore
  },
  {
    id: STEP_IDS.GET_APP_OR_EXTENSION,
    title: "Get the iOS app or Chrome extension",
    tooltip: "Completes when you click one of the links",
    // No matchesOperation - manually triggered from getting-started-card-container when link is clicked
  },
];

/**
 * Union type of all valid step IDs.
 * This provides type safety when passing step IDs to methods like markStepComplete().
 */
export type StepId = (typeof STEP_IDS)[keyof typeof STEP_IDS];
